import {
  ChildContainerItem,
  InfiniteScrollList,
  InfiniteScrollListItem,
  InfiniteScrollMain,
  SearchField,
} from 'components/BusinessListNew/styles';
import usePermission from 'hooks/permission';
import * as all from 'constants/permissions';
import Preloader from 'components/Preloader';
import { useTranslation } from 'react-i18next';
import { LabelName } from 'pages/Report/styles';
import { useMemo, useCallback, useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

const BrandListItems = (props) => {
  const {
    fetchMoreData,
    selectBrand,
    hasMore,
    fetchMoreDataLoading,
    isSearch,
    searchValue,
    activeBrand,
    getItemStyles,
    handleBrandChange,
    displayBrandList,
    fetchMoreDataBySearch,
    paginationSearchBrand,
    searchLoading,
    isShowOnlyLoading,
  } = props;

  const { t } = useTranslation();
  const checkPermission = usePermission();
  const scrollContainerRef = useRef(null);

  // Effect to check if we need to load more data when content doesn't fill container
  useEffect(() => {
    const checkAndLoadMore = () => {
      const container = scrollContainerRef.current;

      // Always log the check attempt for debugging
      console.log('🔍 Auto-load check:', {
        hasContainer: !!container,
        displayBrandListLength: displayBrandList?.length,
        isLessThan10: displayBrandList?.length < 10,
      });

      if (
        container &&
        displayBrandList?.length > 0 &&
        displayBrandList.length < 10
      ) {
        const containerHeight = container.clientHeight;
        const scrollHeight = container.scrollHeight;

        // If content is shorter than container and we have more data to load
        const hasMoreData = isSearch
          ? paginationSearchBrand?.hasNextPage
          : hasMore;

        // Allow for small differences due to borders/padding (up to 20px tolerance)
        const heightTolerance = 20;
        const contentFitsInContainer =
          scrollHeight <= containerHeight + heightTolerance;

        console.log('📏 Container measurements:', {
          containerHeight,
          scrollHeight,
          heightTolerance,
          contentShorter: scrollHeight <= containerHeight,
          contentFitsWithTolerance: contentFitsInContainer,
          hasMoreData,
          fetchMoreDataLoading,
          searchLoading,
          isShowOnlyLoading,
          canAutoLoad:
            contentFitsInContainer &&
            hasMoreData &&
            !fetchMoreDataLoading &&
            !searchLoading &&
            !isShowOnlyLoading,
        });

        if (
          contentFitsInContainer &&
          hasMoreData &&
          !fetchMoreDataLoading &&
          !searchLoading &&
          !isShowOnlyLoading
        ) {
          console.log(
            '🚀 Auto-loading more brands - content shorter than container',
            {
              scrollHeight,
              containerHeight,
              hasMoreData,
              displayBrandListLength: displayBrandList?.length,
              isSearch,
              fetchMoreDataLoading,
              searchLoading,
              isShowOnlyLoading,
            }
          );
          const loadMore = isSearch ? fetchMoreDataBySearch : fetchMoreData;
          if (loadMore) {
            loadMore();
          } else {
            console.warn('⚠️ No loadMore function available');
          }
        }
      }
    };

    // Check after a short delay to allow rendering to complete
    const timeoutId = setTimeout(checkAndLoadMore, 100);

    // Also check after a longer delay in case the first check was too early
    const timeoutId2 = setTimeout(checkAndLoadMore, 500);

    // And one more check after even longer delay for edge cases
    const timeoutId3 = setTimeout(checkAndLoadMore, 1000);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
    };
  }, [
    displayBrandList?.length,
    isSearch,
    paginationSearchBrand?.hasNextPage,
    hasMore,
    fetchMoreDataLoading,
    searchLoading,
    isShowOnlyLoading,
    fetchMoreDataBySearch,
    fetchMoreData,
  ]);

  // Memoized infinite scroll configuration
  const infiniteScrollConfig = useMemo(() => {
    const config = {
      dataLength: displayBrandList?.length || 0,
      next: isSearch ? fetchMoreDataBySearch : fetchMoreData,
      hasMore: isSearch ? paginationSearchBrand?.hasNextPage : hasMore,
      scrollableTarget: 'scrollableDiv',
      height: 205,
      scrollThreshold: 0.7, // Trigger earlier when 70% scrolled down to ensure loading
      endMessage: null, // Prevent end message from showing
      style: {
        border: '1px solid #eee',
        borderRadius: '4px',
      },
      loader: fetchMoreDataLoading ? <Preloader overlay={true} /> : null,
      // Force initial load check if content is shorter than container
      initialScrollY: 0,
    };

    // Debug logging for infinite scroll state (only when there are few items)
    if (config.dataLength < 5) {
      console.log('📊 Infinite scroll config (few items):', {
        dataLength: config.dataLength,
        hasMore: config.hasMore,
        isSearch,
        fetchMoreDataLoading,
        searchLoading,
        isShowOnlyLoading,
      });
    }

    return config;
  }, [
    displayBrandList?.length,
    isSearch,
    fetchMoreDataBySearch,
    fetchMoreData,
    paginationSearchBrand?.hasNextPage,
    hasMore,
    fetchMoreDataLoading,
  ]);

  // Memoized brand selection handler
  const handleSelectBrand = useCallback(
    (item) => {
      selectBrand(item);
    },
    [selectBrand]
  );

  // Memoized list items rendering
  const renderListItems = useMemo(() => {
    if (searchLoading || isShowOnlyLoading) {
      return (
        <InfiniteScrollListItem sx={{ textAlign: 'center', mt: 4 }}>
          <Preloader overlay={true} />
        </InfiniteScrollListItem>
      );
    }

    if (!displayBrandList?.length) {
      return (
        <InfiniteScrollListItem sx={{ textAlign: 'center', color: '#c7c4c4' }}>
          No options
        </InfiniteScrollListItem>
      );
    }

    // Filter out items with missing brandName to prevent empty list items
    const validItems = displayBrandList.filter((item, index) => {
      const isValid = item && item.brandName && item.brandName.trim();
      if (!isValid) {
        console.warn('Invalid brand item at index', index, ':', item);
      }
      return isValid;
    });

    return validItems.map((item, index) => (
      <InfiniteScrollListItem
        onClick={() => handleSelectBrand(item)}
        className={activeBrand(item)}
        sx={getItemStyles(item)}
        key={`${item.brandName}-${item._id || index}`}
      >
        {item.brandName}
      </InfiniteScrollListItem>
    ));
  }, [
    displayBrandList,
    handleSelectBrand,
    activeBrand,
    getItemStyles,
    searchLoading,
    isShowOnlyLoading,
  ]);

  return (
    <ChildContainerItem>
      <LabelName>{t('reports_settings.all_brands')}</LabelName>
      <SearchField
        disabled={checkPermission(all['SUPER'])}
        onChange={handleBrandChange}
        placeholder={t('reports_settings.all_brands_...')}
        value={!checkPermission(all['SUPER']) ? searchValue : ''}
        variant="outlined"
        size="small"
      />
      <InfiniteScrollMain id="scrollableDiv" ref={scrollContainerRef}>
        <InfiniteScroll {...infiniteScrollConfig}>
          <InfiniteScrollList>{renderListItems}</InfiniteScrollList>
        </InfiniteScroll>
      </InfiniteScrollMain>
    </ChildContainerItem>
  );
};

export default BrandListItems;
